import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { CardPage } from "../components/CardPage";
import { LanguageSelect } from "../components/Shell";
import { t } from "../i18n";
import { applyProfile } from "../profile";
import { EditionSignedOut, hasEdition } from "../editions";
import type { SignIn } from "../api";

/** The login page; `go` loads a page anew (a test's to watch). */
export function Login({ go = (to: string) => window.location.assign(to) }: { go?: (to: string) => void } = {}) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // the providers the main server has: none until it says (one without any
  // shows only the email link), and none when it cannot be asked; the email
  // link unless its profile signs no one in so (byj-1wv.114.13)
  const [providers, setProviders] = useState<("google" | "github")[]>([]);
  const [byEmail, setByEmail] = useState(true);
  // a profile whose people come in by a third party's links alone, with an
  // edition of its own: its signed-out page, never this one (RFC-062 D7)
  const [edition, setEdition] = useState<SignIn | null>(null);
  useEffect(() => {
    let live = true;
    api
      .signIn()
      .then((s) => {
        if (!live) return;
        setProviders(s.providers);
        setByEmail(s.email);
        applyProfile(s);
        if (hasEdition(s.ui) && !s.email && s.providers.length === 0) setEdition(s);
      })
      .catch(() => live && setProviders([]));
    return () => {
      live = false;
    };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.magic(email.trim());
      // a demo account is answered with its link and mailed nothing
      // (byj-1wv.84.11): followed at once, so the demo opens as it is asked for
      if (r.demo_link) return go(r.demo_link);
      setSent(true);
      setDevLink(r.dev_link ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (edition) return <EditionSignedOut name={edition.ui!} product={edition.product} signinURL={edition.signin_url} />;
  // the one page outside the shell: the product named over it, as the sidebar names it (byj-1wv.51.7)
  return (
    <CardPage brand title={t("login:loginTitle")} aside={<LanguageSelect />} lead={t("login:loginLead")}>
      {byEmail && (
        <form onSubmit={submit} className="stack">
          <label>
            {t("login:emailLabel")}
            <input
              type="email"
              required
              autoComplete="email"
              placeholder={t("login:emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <button type="submit" className="primary" disabled={busy || !email.trim()}>
            {busy ? t("login:sending") : t("login:sendMagicLink")}
          </button>
        </form>
      )}
      {/* a profile that signs everyone in by entry link (byj-1wv.114.12) */}
      {!byEmail && providers.length === 0 && <p className="muted">{t("login:byLinkOnly")}</p>}
      {sent && <p className="ok-text">{t("login:magicSent")}</p>}
      {devLink && (
        <p>
          {t("login:devLink")}: <a href={devLink}>{devLink}</a>
        </p>
      )}
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {providers.length > 0 && (
        <>
          {byEmail && <p className="divider muted small">{t("login:orContinueWith")}</p>}
          <div className="row" data-testid="providers">
            {providers.includes("google") && (
              <a className="button" href={api.oauthStart("google")}>
                {t("login:google")}
              </a>
            )}
            {providers.includes("github") && (
              <a className="button" href={api.oauthStart("github")}>
                {t("login:github")}
              </a>
            )}
          </div>
        </>
      )}
    </CardPage>
  );
}
